// ticket-statistics-tab.tsx — Statistics tab layout composing all chart sub-components.
import type { StatDateParams } from '@/api/tickets-statistics';
import {
  useTicketStatusSummary,
  useAvgResolutionTimeByPriority,
  useTopTicketHandlers,
  useOverdueTicketsByEmployee,
  useSLACompliance,
  useCustomerSatisfaction,
  useTicketsByType,
  useAssigneeWorkload,
  useMeetingTicketsByPurpose,
  useTicketsByEmployeeAndType,
  useTicketsBySource,
  useTicketsByProduct,
  useTopCustomersByTickets,
} from '@/api/tickets-statistics';
import { TicketStatusPieChart } from './charts/ticket-status-pie-chart';
import { AvgResolutionByPriorityChart } from './charts/avg-resolution-by-priority-chart';
import { TopHandlersTable } from './charts/top-handlers-table';
import { OverdueByEmployeeChart } from './charts/overdue-by-employee-chart';
import { StatusCountByPeriodChart } from './charts/status-count-by-period-chart';
import { SLAComplianceStat } from './charts/sla-compliance-stat';
import { CustomerSatisfactionStat } from './charts/customer-satisfaction-stat';
import { TicketsByTypeChart } from './charts/tickets-by-type-chart';
import { AssigneeWorkloadChart } from './charts/assignee-workload-chart';
import { MeetingByPurposeChart } from './charts/meeting-by-purpose-chart';
import { EmployeeByTypeTable } from './charts/employee-by-type-table';
import { TicketsBySourceChart } from './charts/tickets-by-source-chart';
import { TicketsByProductChart } from './charts/tickets-by-product-chart';
import { TopCustomersByTicketsTable } from './charts/top-customers-by-tickets-table';
import { TicketCreationTrendChart } from './charts/ticket-creation-trend-chart';

const GRID_2: React.CSSProperties = {
  display: 'grid',
  gap: 'var(--vn-space-4)',
  gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
};

const GRID_FULL: React.CSSProperties = {
  display: 'grid',
  gap: 'var(--vn-space-4)',
};

interface Props {
  dateParams: StatDateParams;
}

/**
 * Ticket Statistics tab — all charts are independently data-fetched so a single
 * failing API call degrades only that chart, not the whole tab.
 */
export function TicketStatisticsTab({ dateParams }: Props) {
  const statusSummary       = useTicketStatusSummary(dateParams);
  const avgResolution       = useAvgResolutionTimeByPriority(dateParams);
  const topHandlers         = useTopTicketHandlers(dateParams);
  const overdueByEmployee   = useOverdueTicketsByEmployee(dateParams);
  const slaCompliance       = useSLACompliance(dateParams);
  const customerSat         = useCustomerSatisfaction(dateParams);
  const byType              = useTicketsByType(dateParams);
  const assigneeWorkload    = useAssigneeWorkload(dateParams);
  const meetingByPurpose    = useMeetingTicketsByPurpose(dateParams);
  const byEmployeeAndType   = useTicketsByEmployeeAndType(dateParams);
  const bySource            = useTicketsBySource(dateParams);
  const byProduct           = useTicketsByProduct(dateParams);
  const topCustomers        = useTopCustomersByTickets(dateParams);

  return (
    <div style={GRID_FULL}>
      {/* SLA + Customer Satisfaction — top KPI row */}
      <SLAComplianceStat
        data={slaCompliance.data?.result}
        isLoading={slaCompliance.isLoading}
        isError={slaCompliance.isError}
      />
      <CustomerSatisfactionStat
        data={customerSat.data?.result}
        isLoading={customerSat.isLoading}
        isError={customerSat.isError}
      />

      {/* Volume over time — full width */}
      <StatusCountByPeriodChart dateParams={dateParams} />

      {/* Status pie + Avg resolution side by side */}
      <div style={GRID_2}>
        <TicketStatusPieChart
          data={statusSummary.data?.results}
          isLoading={statusSummary.isLoading}
          isError={statusSummary.isError}
        />
        <AvgResolutionByPriorityChart
          data={avgResolution.data?.results}
          isLoading={avgResolution.isLoading}
          isError={avgResolution.isError}
        />
      </div>

      {/* Tickets by type + Meeting purpose side by side */}
      <div style={GRID_2}>
        <TicketsByTypeChart
          data={byType.data?.results}
          isLoading={byType.isLoading}
          isError={byType.isError}
        />
        <MeetingByPurposeChart
          data={meetingByPurpose.data?.results}
          isLoading={meetingByPurpose.isLoading}
          isError={meetingByPurpose.isError}
        />
      </div>

      {/* Assignee workload — full width */}
      <AssigneeWorkloadChart
        data={assigneeWorkload.data?.results}
        isLoading={assigneeWorkload.isLoading}
        isError={assigneeWorkload.isError}
      />

      {/* Overdue by employee — full width */}
      <OverdueByEmployeeChart
        data={overdueByEmployee.data?.results}
        isLoading={overdueByEmployee.isLoading}
        isError={overdueByEmployee.isError}
      />

      {/* Top handlers table — full width */}
      <TopHandlersTable
        data={topHandlers.data?.results}
        isLoading={topHandlers.isLoading}
        isError={topHandlers.isError}
      />

      {/* Employee × type processing table — full width */}
      <EmployeeByTypeTable
        data={byEmployeeAndType.data?.results}
        isLoading={byEmployeeAndType.isLoading}
        isError={byEmployeeAndType.isError}
      />

      {/* Source + Product side by side */}
      <div style={GRID_2}>
        <TicketsBySourceChart
          data={bySource.data?.results}
          isLoading={bySource.isLoading}
          isError={bySource.isError}
        />
        <TicketsByProductChart
          data={byProduct.data?.results}
          isLoading={byProduct.isLoading}
          isError={byProduct.isError}
        />
      </div>

      {/* Top customers table — full width */}
      <TopCustomersByTicketsTable
        data={topCustomers.data?.results}
        isLoading={topCustomers.isLoading}
        isError={topCustomers.isError}
      />

      {/* Creation trend line — full width */}
      <TicketCreationTrendChart dateParams={dateParams} />
    </div>
  );
}
